import { EntityState } from '@reduxjs/toolkit';

import {
	DrivelineViewDto,
	ProductIndividualType,
	ServiceProtocolDto,
} from 'api';
import { IdLabelDto } from 'api/responses/models/Common/IdLabelDto';
import { DimensionsType } from 'api/responses/models/Unit/UnitMeasurement';
import { Unit } from 'api/responses/models/Unit/UnitStructure/Unit';
import { InstallationDto } from 'api/responses/models/UnitCreation/ProductIndividualSearchResultDto';
import { ProductIndividualSearchResult } from 'domain/productIndividual/types/types';
import { InstallationDrivelineId, ProductId } from 'domain/unit/types/types';
import { ActionDialogChildProps } from 'library/components/controls/dialogs/ActionDialog';
import { Disable, PropWithTestId, Readonly, Removable } from 'library/types';

export type UnitDataFormTypes = {
	unitName?: string | null;
	registrationNumber?: string | null;
	machineId?: string | null;
	hullIdStandard?: string | null;
	hullId?: string | null;
	imoNumber?: string | null;
	mmsiNumber?: string | null;
	callSign?: string | null;
	brand?: IdLabelDto | null;
	customBrand?: string | null;
	model?: IdLabelDto | null;
	customModel?: string | null;
	dimensionsType: DimensionsType;
	length: string;
	lengthUnit: string;
	width: string;
	widthUnit: string;
	displacement: string;
	displacementUnit: string;
	lineOfBusiness: string;
	unitPurpose: string;
	manufacturerParmaId: string | null;
};

export type FileData = {
	readonly name: string;
	readonly serverName: string;
	readonly size: number;
	readonly type: string;
	readonly lastModified: number;
	readonly fileUrl: string;
};

export enum ApiStatus {
	Idle = 0,
	Pending = 1,
	Fulfilled = 2,
	Rejected = 3,
}

export enum UnitConfigurationType {
	CREATION = 'CREATION',
	EDIT = 'EDIT',
}

export interface CommonUnitState {
	status: ApiStatus;
}

export type InstallationStateList = EntityState<Installation, string>;

export interface UnitState extends CommonUnitState {
	activeStep: number;
	installationList: InstallationStateList;
	searchItemsList: EntityState<ProductIndividualSearchResult, string>;
	searchItemWithConfigurationDetected: ProductIndividualSearchResult | null;
	shouldLoadConfigurationForItem: boolean | null;
	type: UnitConfigurationType;
	unitMetadata: UnitMetadata;
}

export interface Installation extends InstallationDto {
	id?: number;
	generatedUuid: string;
}

export type InstallationMetadata = Omit<InstallationDto, 'drivelines'>;

export type MainSegment = 'M' | 'I';

export type ProductIndividualSearchNoResults = {
	errorMessage: string;
	serialNumber: string | null;
};

export type UnitMetadata = {
	lineOfBusiness?: string;
	lineOfBusinessDescription?: string;
	unitPurpose?: string;
	unitPurposeDescription?: string;
	mainSegment: MainSegment | null;
};

export interface DrivelineHeaderActionProps
	extends Removable,
		InstallationDrivelineId,
		PropWithTestId {}

export interface UnitProps {
	unit: Unit;
}

export interface DrivelineProps {
	driveline: DrivelineViewDto;
}

export interface ServiceProtocolProps {
	serviceProtocol: ServiceProtocolDto;
}

export type ForeignProductFormData = Omit<
	ForeignProductIndividualDto,
	'commentsCount' | 'sortPosition' | 'id'
>;

export interface ForeignProductFormSubmitData extends InstallationDrivelineId {
	foreignProduct?: ForeignProductIndividualDto;
}
export interface ProductFormProps<T>
	extends InstallationDrivelineId,
		ActionDialogChildProps {
	onSubmit?: (data: T) => void;
}

export type ForeignProductIndividualDto = {
	serialNumber: string;
	partNumber?: string;
	type: ProductIndividualType;
	description?: string;
	generatedUuid: string;
	commentsCount: number;
	sortPosition: number;
	id: number;
};

export interface ProductCardActionProps
	extends Disable,
		Readonly,
		InstallationDrivelineId,
		ProductId {
	generatedUuid: string;
}
